import AddIcon from "@mui/icons-material/Add";
import { Button, Chip } from "@mui/joy";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import React from "react";
import { Util } from "../Helpers/Util";
import { Elevator } from "../Interfaces/Elevator";
import InspectionReportDialog from "../Dialogs/InspectionReportDialog";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import PortalTableLayout from "../components/PortalTableLayout";

interface InspectionReportsListingProps {
	buildingId: string;
	readOnly?: boolean;
}

export interface InspectionReport {
	inspection_reports_id: string;
	buildings_id: string;
	date_entered: Date;
	file_name: string;
	elevator: string;
	serial_number?: string;
	elevators_id: string;
	description: string;
	is_passed?: boolean;
}

interface ReportRow {
	key: string;
	description: string;
	date: Date | null;
	elevator: string;
	serial_number: string;
	source: "manual" | "attempt";
	inspection_reports_id?: string;
	blob_url?: string;
	raw?: InspectionReport;
	passed: boolean | null;
}

function triStatePass(v: unknown): boolean | null {
	if (v === null || v === undefined) return null;
	if (typeof v === "boolean") return v;
	if (typeof v === "number") return v !== 0;
	const s = String(v).toLowerCase();
	if (s === "true" || s === "1") return true;
	if (s === "false" || s === "0") return false;
	return null;
}

export default function InspectionReportsListing(props: InspectionReportsListingProps) {
	const [inspections, setInspections] = React.useState([] as Array<InspectionReport>);
	const [inspection, setInspection] = React.useState<InspectionReport>({} as InspectionReport);
	const [dialog, setDialog] = React.useState(false);
	const [elevators, setElevators] = React.useState<Array<Elevator>>([]);
	const [attemptPdfs, setAttemptPdfs] = React.useState<Array<Record<string, unknown>>>([]);
	const [sortBy, setSortBy] = React.useState<"date" | "elevator">("date");
	const [sortDir, setSortDir] = React.useState<"asc" | "desc">("desc");

	React.useEffect(() => {
		getData();
		getElevators();
		getAttemptPdfs();
	}, [props.buildingId]);

	const getElevators = () => {
		fetch("Building/GetElevators", {
			method: "Post",
			cache: "no-cache",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ buildingsId: props.buildingId }),
		})
			.then((x) => x.json())
			.then((x) => setElevators(x));
	};

	const getData = () => {
		fetch("Inspection/GetInspections", {
			method: "Post",
			cache: "no-cache",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(props.buildingId),
		})
			.then((x) => x.json())
			.then((x) => setInspections(x as Array<InspectionReport>));
	};

	const getAttemptPdfs = () => {
		fetch("Inspection/GetInspectionAttemptPdfsForBuilding", {
			method: "Post",
			cache: "no-cache",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(props.buildingId),
		})
			.then((x) => x.json())
			.then((x) => setAttemptPdfs(x as Array<Record<string, unknown>>));
	};

	const openInspection = (row: ReportRow) => {
		if (row.source === "manual" && row.inspection_reports_id) {
			window.open(document.location.origin + "/Inspection/" + row.inspection_reports_id, "_blank");
		} else if (row.source === "attempt" && row.blob_url) {
			window.open(row.blob_url, "_blank");
		}
	};

	const attemptLabels = ["First", "Second", "Third", "Fourth", "Fifth", "Sixth", "Seventh", "Eighth", "Ninth", "Tenth"];

	const baseRows: Array<ReportRow> = [
		...inspections.map((x) => ({
			key: x.inspection_reports_id,
			description: Util.stripPdfExtension(x.description || x.file_name || ""),
			date: x.date_entered ? new Date(x.date_entered) : null,
			elevator: x.elevator,
			serial_number: (x.serial_number ?? x.elevator ?? "").toString(),
			source: "manual" as const,
			inspection_reports_id: x.inspection_reports_id,
			raw: x,
			passed: x.is_passed === undefined || x.is_passed === null ? false : !!x.is_passed,
		})),
		...attemptPdfs.map((x) => {
			const n = (x.attempt_number as number) ?? 1;
			const label = attemptLabels[n - 1] ?? String(n);
			const fn = Util.stripPdfExtension(String(x.file_name ?? ""));
			return {
				key: String(x.inspection_attempt_pdf_id),
				description: `${label} Inspection — ${fn}`,
				date: x.attempt_inspection_date ? new Date(x.attempt_inspection_date as string) : null,
				elevator: String(x.elevator ?? ""),
				serial_number: String(x.serial_number ?? x.elevator ?? ""),
				source: "attempt" as const,
				blob_url: x.blob_url as string,
				passed: triStatePass(x.attempt_passed),
			};
		}),
	];

	const compareRows = (a: ReportRow, b: ReportRow) => {
		const dir = sortDir === "asc" ? 1 : -1;
		if (sortBy === "elevator") {
			const c = a.serial_number.localeCompare(b.serial_number, undefined, { numeric: true, sensitivity: "base" });
			if (c !== 0) return c * dir;
			const da = a.date?.getTime() ?? -Infinity;
			const db = b.date?.getTime() ?? -Infinity;
			return (da - db) * dir;
		}
		const da = a.date?.getTime() ?? null;
		const db = b.date?.getTime() ?? null;
		if (da == null && db == null) return 0;
		if (da == null) return 1;
		if (db == null) return -1;
		const cmp = (da - db) * dir;
		if (cmp !== 0) return cmp;
		return a.serial_number.localeCompare(b.serial_number, undefined, { numeric: true, sensitivity: "base" }) * dir;
	};

	const rows = [...baseRows].sort(compareRows);

	const toggleSort = (col: "date" | "elevator") => {
		if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
		else {
			setSortBy(col);
			setSortDir(col === "date" ? "desc" : "asc");
		}
	};

	const passChip = (p: boolean | null) => {
		if (p === null) {
			return (
				<Chip size="sm" variant="soft" color="neutral">
					—
				</Chip>
			);
		}
		return p ? (
			<Chip size="sm" variant="soft" color="success">
				Pass
			</Chip>
		) : (
			<Chip size="sm" variant="soft" color="danger">
				Fail
			</Chip>
		);
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDateFns}>
			{!props.readOnly && (
				<InspectionReportDialog
					buildingId={props.buildingId}
					open={dialog}
					inspectionReport={inspection}
					close={() => {
						setDialog(false);
						getData();
						getAttemptPdfs();
					}}
					elevators={elevators}
				/>
			)}
			<PortalTableLayout
				fillHeight={false}
				toolbar={
					!props.readOnly ? (
						<Button
							sx={{ mt: 1 }}
							startDecorator={<AddIcon />}
							onClick={() => {
								setInspection({} as InspectionReport);
								setDialog(true);
							}}>
							Add Report
						</Button>
					) : undefined
				}>
				<Table stickyHeader size="small">
					<TableHead>
						<TableRow>
							<TableCell>Inspection</TableCell>
							<TableCell sortDirection={sortBy === "date" ? sortDir : false}>
								<TableSortLabel
									active={sortBy === "date"}
									direction={sortBy === "date" ? sortDir : "asc"}
									onClick={(e) => {
										e.stopPropagation();
										toggleSort("date");
									}}>
									Inspection date
								</TableSortLabel>
							</TableCell>
							<TableCell sortDirection={sortBy === "elevator" ? sortDir : false}>
								<TableSortLabel
									active={sortBy === "elevator"}
									direction={sortBy === "elevator" ? sortDir : "asc"}
									onClick={(e) => {
										e.stopPropagation();
										toggleSort("elevator");
									}}>
									Elevator
								</TableSortLabel>
							</TableCell>
							<TableCell>Result</TableCell>
							{!props.readOnly && <TableCell>Edit</TableCell>}
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.map((x) => (
							<TableRow key={x.key} hover sx={{ cursor: "pointer" }} onClick={() => openInspection(x)}>
								<TableCell>{x.description}</TableCell>
								<TableCell>{x.date ? Util.Formatter(x.date, "D") : ""}</TableCell>
								<TableCell>{x.elevator}</TableCell>
								<TableCell onClick={(e) => e.stopPropagation()}>{passChip(x.passed)}</TableCell>
								{!props.readOnly && (
									<TableCell>
										{x.source === "manual" && x.raw && (
											<Button
												onClick={(e) => {
													e.preventDefault();
													e.stopPropagation();
													setInspection(x.raw!);
													setDialog(true);
												}}>
												Edit
											</Button>
										)}
									</TableCell>
								)}
							</TableRow>
						))}
					</TableBody>
				</Table>
			</PortalTableLayout>
		</LocalizationProvider>
	);
}
